import { useState } from 'react';
import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { PictureSelector } from 'components/common';
import { Icon } from 'components/common/icons';
import { DisplayField, FormGroup, FormSection } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

import type { CoursePicturePosition } from 'shared-types';

interface Props {
  picture: string;
  onFileAdded: (value: boolean) => void;
  setUploader: (fileUploader: React.ReactElement<any>) => void;
}

const PathSettingsImage = ({ picture, onFileAdded, setUploader }: Props) => {
  const formApi = useFormApi();
  const formState = useFormState();

  const { messages, theme } = useReduxState();

  const [preview, setPreview] = useState(null);

  if (!formApi) return null;

  const handleFileRemoved = () => {
    formApi.setValue('picture', picture);
    setPreview(picture);
  };

  const handlePicturePosition = (value: CoursePicturePosition) => {
    formApi.setValue('picturePosition', value);
  };

  const handlePredefinedImage = (url: string) => {
    formApi.setValue('picture', url);
    setPreview(url);
    onFileAdded(false);
  };

  const handleThumbnail = ({ preview }) => {
    formApi.setValue('picture', '');
    setPreview(preview);
    onFileAdded(true);
  };

  const picturePositionOptions = [
    { value: 'left top', icon: 'long-arrow-alt-left' as const, rotate: 45 },
    { value: 'center top', icon: 'long-arrow-alt-left' as const, rotate: 90 },
    { value: 'right top', icon: 'long-arrow-alt-left' as const, rotate: 135 },
    { value: 'left center', icon: 'long-arrow-alt-left' as const, rotate: 0 },
    { value: 'center center', icon: 'arrows-alt' as const, rotate: 0 },
    {
      value: 'right center',
      icon: 'long-arrow-alt-left' as const,
      rotate: 180
    },
    { value: 'left bottom', icon: 'long-arrow-alt-left' as const, rotate: 315 },
    {
      value: 'center bottom',
      icon: 'long-arrow-alt-left' as const,
      rotate: 270
    },
    { value: 'right bottom', icon: 'long-arrow-alt-left' as const, rotate: 225 }
  ];

  return (
    <FormSection title={messages.blocksImage}>
      <DisplayField field="picture">
        <div className="actions">
          <div className="uploader">
            <FileUploader
              allowedFileTypes={['image/*']}
              getUploader={setUploader}
              icon="file"
              id={`image-uploader`}
              keyPrefix="uploads/images/original/"
              onFileRemoved={handleFileRemoved}
              onThumbnailGenerated={handleThumbnail}
              uuidAsFilename={true}
            />
          </div>

          <div className="or">{messages.commonOr.toUpperCase()}</div>

          <PictureSelector
            onSelect={handlePredefinedImage}
            picturePosition={
              formState.values?.picturePosition as CoursePicturePosition
            }
            selectedPicture={preview || formState.values?.picture}
          />
        </div>
      </DisplayField>

      {preview || formState.values?.picture ? (
        <div className="preview">
          <div
            className="preview-image"
            style={{
              backgroundImage: `url(${preview || formState.values?.picture})`
            }}
          />
        </div>
      ) : null}

      <FormGroup
        label={messages.settingsCoursePicturePosition}
        labelTooltip={messages.settingsCoursePicturePositionInfo}
      >
        <DisplayField field="picturePosition">
          {picturePositionOptions.map((option, i) => (
            <button
              className={`button ${
                formState.values?.picturePosition === option.value
                  ? 'active'
                  : ''
              }`}
              data-position={option.value}
              key={i}
              onClick={() =>
                handlePicturePosition(option.value as CoursePicturePosition)
              }
              type="button"
            >
              <Icon icon={option.icon} transform={{ rotate: option.rotate }} />
            </button>
          ))}
        </DisplayField>
      </FormGroup>

      <style jsx>{`
        .actions {
          display: flex;
          gap: 10px;
        }
        .or {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          font-weight: 600;
          padding: 5px 0;
        }
        .options {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          width: 400px;
        }
        .option {
          display: flex;
          justify-content: center;
          width: 25%;
        }
        .options button.option-button {
          min-width: 0;
        }
        .option-content {
          height: 42px;
          position: relative;
        }
        .preview-image {
          background-position: ${formState.values?.picturePosition ||
          'center top'};
          background-size: cover;
          border-radius: ${theme.cardBorderRadius};
          margin-top: 10px;
          position: relative;
          height: 140px;
          width: 100%;
        }
        .positions {
          margin-top: 10px;
        }
        .button {
          background-color: ${theme.lightGrey};
          border: 3px solid white;
          border-radius: 8px;
          color: ${theme.textColorLight};
          cursor: pointer;
          font-size: 1.6rem;
          height: 38px;
          outline: 0;
          padding: 3px 0;
          transition: border-color 0.2s;
          width: 38px;
        }
        .button:focus,
        .button:hover {
          border-color: #ccc;
        }
        .button.active {
          border-color: #555;
        }
        .button:not(:first-child) {
          margin-left: 5px;
        }
        @media (max-width: ${breakpoints.medium}px) {
          .options {
            width: 200px;
          }
          .option {
            width: 50%;
          }
        }
      `}</style>
    </FormSection>
  );
};

export default PathSettingsImage;
